import { Button, Slide, SxProps, Theme, useTheme } from '@mui/material';
import cn from 'classnames';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { animated } from 'react-spring';

import ScaleInOut from '../../animations/ScaleInOut';
import { useCurrency } from '../../contexts/CurrencyProvider';
import useWindowSize from '../../hooks/useWindowSize';
import { BUTTON_BUTTON } from '../../types/forms';
import { IInventoryItem, InventoryTypesEnum } from '../../types/inventories';
import { ItemMetadata, Price } from '../index';
import { QuantitySelector } from '../_mui';

import styles from './InventoryItem.module.scss';

export interface IInventoryItemProps
  extends Omit<IInventoryItem, 'item_id' | 'department_ids' | 'description' | 'org_id' | 'name' | 'image_url'> {
  className?: string;
  itemId?: string;
  icon?: any;
  title?: string;
  price?: number;
  iconClassName?: string;
  infoClassName?: string;
  onEdit?: () => void;
  onDelete?: () => void;
  onClick?: () => void;
  count?: number;
  isPreorder?: boolean;
  type: InventoryTypesEnum;
  isCustomizable?: boolean;
  skuOptions?: IInventoryItem[];
  onSelect?: () => void;
  isDisabled?: boolean;
  isSelected?: boolean;
  hint?: string;
  threshold?: number;
  index?: number;
  selectedQuantity?: number;
  onQuantityChange?: (itemId: string, quantity: number) => void;
}

const InventoryItem: React.FC<IInventoryItemProps> = ({
  className,
  itemId,
  icon,
  title,
  price,
  currency_code,
  iconClassName,
  infoClassName,
  onEdit,
  onDelete,
  onClick,
  count,
  isPreorder,
  isCustomizable,
  skuOptions,
  type,
  onSelect,
  isDisabled,
  isSelected,
  fulfillment_center_id,
  hint,
  threshold,
  index,
  selectedQuantity,
  onQuantityChange,
}: IInventoryItemProps) => {
  const { mobile } = useWindowSize();
  const { getItemPrice } = useCurrency();
  const [isHovered, setIsHovered] = useState(false);
  const theme = useTheme();

  // Local state for fallback when parent doesn't provide controlled state
  const [localQuantity, setLocalQuantity] = useState<number | undefined>(undefined);
  const isRemovingRef = useRef(false);

  // Use parent's selectedQuantity if provided, otherwise use local state
  const currentQuantity = useMemo(() => selectedQuantity ?? localQuantity, [selectedQuantity, localQuantity]);

  const showQuantitySelector = useMemo(() => !!currentQuantity && currentQuantity > 0, [currentQuantity]);

  const item = useMemo(
    () => ({
      item_id: itemId,
      name: title,
      type,
      count,
      sku_options: skuOptions,
      fulfillment_center_id,
      item_customization_allowed: isCustomizable,
      preorder_allowed: isPreorder,
      low_stock_threshold: threshold,
    }),
    [itemId, title, type, count, skuOptions, fulfillment_center_id, isCustomizable, isPreorder, threshold],
  );

  const itemDisplayedPrice = useMemo(
    () => getItemPrice({ price, type, sku_options: skuOptions, currency_code }),
    [type, price, skuOptions, currency_code, getItemPrice],
  );

  const onEditClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      if (typeof onEdit === 'function') {
        onEdit();
      }
    },
    [onEdit],
  );

  const onDeleteClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      if (typeof onDelete === 'function') {
        onDelete();
      }
    },
    [onDelete],
  );

  const handleSelectItem = useCallback(
    (e?: React.MouseEvent) => {
      if (e) {
        e.stopPropagation();
      }
      if (!isDisabled && itemId) {
        // For new logic with quantity selector
        if (onQuantityChange) {
          setLocalQuantity(1);
          onQuantityChange(itemId, 1);
        }
        // For old logic without quantity selector
        else if (onSelect) {
          onSelect();
        }
      }
    },
    [isDisabled, itemId, onQuantityChange, onSelect],
  );

  const handleQuantityChangeInternal = useCallback(
    (value?: number) => {
      // Ignore changes when item is being removed to prevent re-adding
      if (isRemovingRef.current) {
        return;
      }

      if (value && itemId) {
        setLocalQuantity(value);
        // call parent callback
        if (onQuantityChange) {
          onQuantityChange(itemId, value);
        }
      }
    },
    [itemId, onQuantityChange],
  );

  const handleQuantityDelete = useCallback(() => {
    if (itemId) {
      // Update local state immediately
      setLocalQuantity(undefined);
      // Also call parent callback if provided
      if (onQuantityChange) {
        onQuantityChange(itemId, 0);
      }
    }
  }, [itemId, onQuantityChange]);

  const controlsMarkup = useMemo(() => {
    if ((onDelete || onEdit) && !onSelect) {
      return (
        <div className={cn(styles.controls, { [styles.mobile]: mobile })}>
          {onEdit && (
            <div role="button" className={cn(styles.controlButton, styles.edit)} onClick={onEditClick}>
              Edit
            </div>
          )}
          {onDelete && (
            <div role="button" className={cn(styles.controlButton, styles.delete)} onClick={onDeleteClick}>
              Delete
            </div>
          )}
        </div>
      );
    }

    return null;
  }, [onDelete, onEdit, mobile, onEditClick, onDeleteClick, onSelect]);

  const dataAttribute = React.useMemo(() => {
    if (isSelected && isHovered) {
      return 'item_minus_btn';
    }
    if (isSelected) {
      return 'item_mark_btn';
    }
    return 'item_plus_btn';
  }, [isSelected, isHovered]);

  // Sync local state when parent removes the item (selectedQuantity becomes undefined)
  useEffect(() => {
    if (selectedQuantity === undefined) {
      isRemovingRef.current = true;
      setLocalQuantity(undefined);
    } else {
      isRemovingRef.current = false;
    }
  }, [selectedQuantity]);

  const addButtonStyles: SxProps<Theme> = () => ({
    position: 'absolute',
    bottom: 8,
    right: 8,
    minWidth: 'unset',
    padding: theme.spacing(0.75, 1.5),
    fontSize: 14,
    textTransform: 'none',
    '&.Mui-disabled': {
      opacity: 1,
      backgroundColor: theme.palette.grey[300],
      color: theme.palette.action.disabled,
    },
  });

  const quantitySelectorStyles: SxProps<Theme> = {
    width: '100%',
    height: 46,
    backgroundColor: theme.palette.common.white,
    boxShadow: theme.sass.topShadow,
    alignItems: 'center',
    justifyContent: 'center',
    paddingX: 1,
    '& .MuiInputBase-root': {
      width: 94,
    },
  };

  const oldAddButton = useMemo(() => {
    return (
      <ScaleInOut trigger={!isDisabled}>
        {({ style }) => (
          <animated.div
            role={BUTTON_BUTTON}
            style={style}
            data-action={dataAttribute}
            className={cn(styles.addBtn, isSelected ? styles.selected : '', isDisabled ? styles.disabled : '')}
            onClick={handleSelectItem}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          />
        )}
      </ScaleInOut>
    );
  }, [isDisabled, dataAttribute, handleSelectItem, isSelected]);

  return (
    <div
      className={cn(className, styles.inventoryItem, {
        [styles.clickable]: onClick && !isDisabled,
        [styles.disabled]: isDisabled,
      })}
      onClick={!isDisabled ? onClick : void 0}
      title={hint}
      data-action="item-btn"
      data-index={index}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <div className={styles.innerWrapper}>
        <div className={cn(iconClassName, styles.iconContainer)}>
          <div
            className={cn(styles.icon, { [styles.disabled]: isDisabled })}
            style={{ backgroundImage: `url("${icon}")` }}
          />
          <ItemMetadata item={item} showCount={true} />
          {onSelect && onQuantityChange && (
            <>
              {!showQuantitySelector && (
                <Button
                  variant="contained"
                  onClick={handleSelectItem}
                  disabled={isDisabled}
                  sx={addButtonStyles}
                  data-action="item_plus_btn"
                >
                  Add
                </Button>
              )}
              <Slide direction="left" in={showQuantitySelector} mountOnEnter unmountOnExit>
                <div
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    width: '100%',
                  }}
                >
                  <QuantitySelector
                    value={currentQuantity || 1}
                    onChange={handleQuantityChangeInternal}
                    onDelete={handleQuantityDelete}
                    disabled={isDisabled}
                    sx={quantitySelectorStyles}
                  />
                </div>
              </Slide>
            </>
          )}
          {onSelect && !onQuantityChange && oldAddButton}
        </div>
        {!mobile && controlsMarkup}
        <div className={cn(styles.info, infoClassName, { [styles.disabled]: isDisabled })}>
          <div className={styles.titleAndPrice}>
            <Price value={itemDisplayedPrice} className={styles.price} />
            <div className={styles.title} title={title}>
              {title}
            </div>
          </div>
          {mobile && controlsMarkup}
        </div>
      </div>
    </div>
  );
};

export default InventoryItem;
